import React, { useEffect } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Appstore/store";
import {
  UpdateSmsgateways,
  GetSmsGatewayView,
} from "../Features/SmsGatewaysdata";
import { GetSmsGateways } from "../Features/Smsgatewaylist";
import { setIsLoading } from "../Features/loadingSlice";
import { IoMdArrowRoundBack } from "react-icons/io";
import { toast } from "sonner";
import { MdContentCopy } from "react-icons/md";


interface PhoneNumber {
  gateway_number: string;
}

interface FormValues {
  name?: string;
  slug?: string;
  sendinglimit?: number | string;
  phoneNumber?: string;
  gatewayid?: number;
  accountsid?: string;
  authtoken?: string;
  apiKey?: string;
  publicKey?: string;
}

const Updatesmsgateways: React.FC = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { id }: any = useParams();
  const { smsgatewaylist } = useSelector(
    (state: RootState) => state.smsgatewaylist
  );
  const { smsgatewaysdata, loading } = useSelector(
    (state: RootState) => state.smsgateways
  );
  const record = smsgatewaysdata.find((gateway) => gateway.id === Number(id));
  const existingPhoneNumbers =
    smsgatewaysdata?.map((gateway) => gateway.phone_numbers).flat() || [];

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, "Name must be at least 3 characters")
      .max(250, "Name must be at most 250 characters")
      .required("Name is required")
      .trim(),
    slug: Yup.string()
      .min(2, 'Slug must be at least 2 characters')
      .matches(
        /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
        "Slug must contain only lowercase letters, numbers, and hyphens, and cannot start or end with a hyphen"
      )
      .required("Slug is required"),
    sendinglimit: Yup.number().typeError("Sending limit must be a number"),
    phoneNumber: Yup.string()
      .matches(
        /^(\+\d{1,3}\d{10,})(,\+\d{1,3}\d{10,})*$/,
        "Must start with a + followed by country code and space is not allow and at least 10 digits, separated by commas"
      )
      .required("Mobile number is required")
      .test("duplicate", "Phone number already exists", function (value) {
        if (!value) return true;
        const phoneNumbers = value.split(",").map((num) => num.trim());
        const isDuplicate = phoneNumbers.some((num) =>
          existingPhoneNumbers.includes(num)
        );
        const hasInternalDuplicates =
          phoneNumbers.length !== new Set(phoneNumbers).size;
        return !isDuplicate && !hasInternalDuplicates;
      }),
    gatewayid: Yup.number().required("Gateway Name is required"),
    accountsid: Yup.string().when("gatewayid", (gatewayid, schema) => {
      return Number(gatewayid) === 1
        ? schema.required("Account SID is required").max(250)
        : schema.max(250);
    }),
    authtoken: Yup.string().when("gatewayid", (gatewayid, schema) => {
      return Number(gatewayid) === 1
        ? schema.required("Auth Token is required").max(250)
        : schema.max(250);
    }),
    apiKey: Yup.string().when("gatewayid", (gatewayid, schema) => {
      return Number(gatewayid) === 2
        ? schema.required("Api Key is required").max(250)
        : schema.max(250);
    }),
    publicKey: Yup.string().when("gatewayid", (gatewayid, schema) => {
      return Number(gatewayid) === 2
        ? schema.required("Public Key is required").max(250)
        : schema.max(250);
    }),
  });

  useEffect(() => {
    if (id) {
      dispatch(setIsLoading(true));
      dispatch(GetSmsGatewayView(id))
        .unwrap()
        .catch((err) => {
          console.error("Error fetching agent monitor:", err);
        })
        .finally(() => {
          setTimeout(() => dispatch(setIsLoading(false)), 500);
        });
    }
  }, [id, dispatch]);

  useEffect(() => {
    dispatch(GetSmsGateways({ search: "", page: 1, limit: 10 }));
  }, [dispatch]);

  const initialValues: FormValues = {
    name: record?.name || "",
    slug: record?.slug || "",
    sendinglimit: record?.sending_limit !== null ? record?.sending_limit : "",
    gatewayid: record?.gateway_id,
    accountsid: record?.account_sid || "",
    authtoken: record?.auth_token || "",
    phoneNumber: Array.isArray(record?.phone_numbers)
      ? record?.phone_numbers
        .map((num: PhoneNumber) => num.gateway_number)
        .join(",")
      : "",
    apiKey: record?.api_key || "",
    publicKey: record?.public_key || "",
  };

  const navigatetobackpage = () => {
    navigate(-1);
  };


  return (
    <>
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content">
            <div className="container-fluid">
              <div className="card mt-4">
                <div className="card-header bg-white p-3 d-flex justify-content-md-between">
                  <h2 className="w-100 mb-md-0 fs-3">Update SMS Gateway</h2>
                  <Link to={""} className="text-decoration-none">
                    <button
                      onClick={navigatetobackpage}
                      className="btn btn-info text-white d-flex align-items-center fw-semibold"
                    >
                      <IoMdArrowRoundBack /> Back
                    </button>
                  </Link>
                </div>
                <div className="card-body">
                  <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={async (
                      values: FormValues,
                      { setSubmitting }: FormikHelpers<FormValues>
                    ) => {
                      const { id }: any = record;
                      const smsGatewaysData = {
                        name: values.name,
                        slug: values.slug,
                        sending_limit: values.sendinglimit
                          ? Number(values.sendinglimit)
                          : null,
                        gateway_id: Number(values.gatewayid),
                        account_sid: values.accountsid,
                        auth_token: values.authtoken,
                        phone_numbers: values.phoneNumber,
                        api_key: values.apiKey,
                        public_key: values.publicKey,
                      };

                      try {
                        const response: any = await dispatch(
                          UpdateSmsgateways({
                            id: id ?? 0,
                            updatedData: smsGatewaysData,
                          })
                        );
                        if (
                          response.payload &&
                          response.payload.status === "success"
                        ) {
                          toast.success(response.payload.message);
                          navigate("/admin/smsgateways");
                        } else {
                          toast.error(response.payload.message);
                        }
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      isSubmitting,
                    }) => (
                      <Form>
                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="name">
                              Name<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${touched.name && errors.name ? "is-invalid" : ""
                                }`}
                              id="name"
                              name="name"
                              placeholder="Enter Name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.name && errors.name && (
                              <div className="text-danger">{errors.name}</div>
                            )}
                          </div>

                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="gatewayid">
                              Select Gateway
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              name="gatewayid"
                              value={values.gatewayid}
                              onChange={handleChange}
                              className={`form-select shadow-none ${touched.gatewayid && errors.gatewayid
                                ? "is-invalid"
                                : ""
                                }`}
                            >
                              <option
                                value="Select Gateway"
                                label="Select Gateway"
                                selected
                                className="d-none"
                              />
                              {loading && <option>Loading...</option>}
                              {smsgatewaylist.map((gateway) => (
                                <option key={gateway.id} value={gateway.id}>
                                  {gateway.name}
                                </option>
                              ))}
                            </select>
                            {touched.gatewayid && errors.gatewayid && (
                              <div className="text-danger">
                                {errors.gatewayid}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="accountsid">Account SID</label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${touched.accountsid && errors.accountsid
                                ? "is-invalid"
                                : ""
                                }`}
                              id="accountsid"
                              name="accountsid"
                              placeholder="Enter Account SID"
                              value={values.accountsid}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.accountsid && errors.accountsid && (
                              <div className="text-danger">
                                {errors.accountsid}
                              </div>
                            )}
                          </div>

                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="authtoken">Auth Token</label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${touched.authtoken && errors.authtoken
                                ? "is-invalid"
                                : ""
                                }`}
                              id="authtoken"
                              name="authtoken"
                              placeholder="Enter a Auth Token"
                              value={values.authtoken}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.authtoken && errors.authtoken && (
                              <div className="text-danger">
                                {errors.authtoken}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label htmlFor="apiKey">Api Key</label>
                              <input
                                type="text"
                                className={`form-control shadow-none ${touched.apiKey && errors.apiKey
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                id="apiKey"
                                name="apiKey"
                                placeholder="Enter an API Key"
                                value={values.apiKey}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.apiKey && errors.apiKey && (
                                <div className="text-danger">
                                  {errors.apiKey}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label htmlFor="publicKey">Public Key</label>
                              <input
                                type="text"
                                className={`form-control shadow-none ${touched.publicKey && errors.publicKey
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                id="publicKey"
                                name="publicKey"
                                placeholder="Enter an Public Key"
                                value={values.publicKey}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.publicKey && errors.publicKey && (
                                <div className="text-danger">
                                  {errors.publicKey}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label htmlFor="sendinglimit">Sending Limit</label>
                              <input
                                type="text"
                                className={`form-control shadow-none ${touched.sendinglimit && errors.sendinglimit
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                id="sendinglimit"
                                name="sendinglimit"
                                placeholder="Enter Sending Limit (optional)"
                                value={values.sendinglimit}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.sendinglimit && errors.sendinglimit && (
                                <div className="text-danger">
                                  {errors.sendinglimit}
                                </div>
                              )}
                            </div>

                            <div className="form-group mb-3">
                              <label htmlFor="slug">
                                Slug<span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control shadow-none ${touched.slug && errors.slug ? "is-invalid" : ""
                                  }`}
                                id="slug"
                                name="slug"
                                placeholder="Enter Slug Name"
                                value={values.slug}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <small><b>Note:</b> This slug is used in webhooks. If you update this slug, you'll need to change your webhook URL in the SMS gateway. Otherwise, you might not receive new incoming messages.</small>

                              {values.gatewayid == 1 && (
                                <small className="d-flex justify-content-between align-items-center d-block p-1 px-2 rounded fw-semibold" style={{ backgroundColor: 'rgba(212, 213, 215, 0.5)' }}>{apiUrl}/api/web-hooks/twilio/new-message/arrived?gateway={values.slug} <MdContentCopy
                                  role="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(`${apiUrl}/api/web-hooks/twilio/new-message/arrived?gateway=${values.slug}`);
                                    toast.success("Webhook URL copied!");
                                  }}
                                  title="Copy to clipboard"
                                  style={{ cursor: "pointer" }}
                                /></small>
                              )}

                              {values.gatewayid == 2 && (
                                <small className="d-flex justify-content-between align-items-center d-block p-1 px-2 rounded fw-semibold" style={{ backgroundColor: 'rgba(212, 213, 215, 0.5)' }}>{apiUrl}/api/web-hooks/telnyx?gateway={values.slug}<MdContentCopy
                                  role="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(`${apiUrl}/api/web-hooks/telnyx?gateway=${values.slug}`);
                                    toast.success("Webhook URL copied!");
                                  }}
                                  title="Copy to clipboard"
                                  style={{ cursor: "pointer" }}
                                /></small>
                              )}

                              {values.gatewayid === 0 && (
                                <small className=" d-block"></small>
                              )}

                              {touched.slug && errors.slug && (
                                <div className="text-danger">{errors.slug}</div>
                              )}
                            </div>
                          </div>

                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="phoneNumber">
                              Phone Number<span className="text-danger">*</span>
                            </label>
                            <textarea
                              className={`form-control phonenumber-text-area-height shadow-none ${touched.phoneNumber && errors.phoneNumber
                                ? "is-invalid"
                                : ""
                                }`}
                              id="phoneNumber"
                              name="phoneNumber"
                              placeholder="Enter Phone Number"
                              value={values.phoneNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              rows={5}
                              style={{ resize: "none" }}
                            />
                            {touched.phoneNumber && errors.phoneNumber && (
                              <div className="text-danger">
                                {errors.phoneNumber}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="d-flex">
                          <button
                            type="submit"
                            className="btn btn-primary me-2"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                <span className="ps-2">Save...</span>
                              </>
                            ) : (
                              "Save"
                            )}
                          </button>
                          <Link
                            to="/admin/smsgateways"
                            className="btn btn-secondary"
                          >
                            Cancel
                          </Link>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Updatesmsgateways;
